<script>
import { bearing, fleetStateColor, straightLineKm } from '@/utils/fleetMarker'

// Os avisos do Frotas chegam a cada poucos segundos: a viatura desliza até a nova posição em vez de pular.
const GLIDE_MS = 1200
// Abaixo disso é ruído de GPS parado; girar a seta por ele só faria a viatura tremer.
const MIN_HEADING_KM = 0.004

// Pino genérico, para tipo de veículo sem imagem cadastrada: ambulância de perfil.
const GLYPH = '<svg class="fvm__glyph" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" '
  + 'd="M2 6h12v9H2zM14 9h4l3 3v3h-7z"/><circle cx="7" cy="17" r="2" fill="currentColor"/>'
  + '<circle cx="17" cy="17" r="2" fill="currentColor"/><path fill="#fff" d="M7.2 8h1.6v1.6h1.6v1.6H8.8v1.6H7.2v-1.6H5.6V9.6h1.6z"/></svg>'

let OverlayClass = null

/** O Google só existe depois de carregar o mapa: a classe é criada na primeira vez que alguém a usa. */
function overlayClass() {
  if (OverlayClass) return OverlayClass

  const { google } = window

  OverlayClass = class VehicleOverlay extends google.maps.OverlayView {
    constructor(el, point) {
      super()
      this.el = el
      this.point = point
    }

    onAdd() {
      this.getPanes().overlayMouseTarget.appendChild(this.el)
    }

    draw() {
      const projection = this.getProjection()
      if (!projection) return

      const px = projection.fromLatLngToDivPixel(new google.maps.LatLng(this.point.lat, this.point.lng))
      if (!px) return

      this.el.style.left = `${px.x}px`
      this.el.style.top = `${px.y}px`
    }

    onRemove() {
      if (this.el.parentNode) this.el.parentNode.removeChild(this.el)
    }

    moveTo(point) {
      this.point = point
      this.draw()
    }
  }

  return OverlayClass
}

/**
 * Viatura no mapa: selo com a imagem do tipo de veículo (USA, USB...), anel na cor do estado, seta para onde
 * ela anda e o nome da equipe. Desliza entre as posições.
 *
 * É um elemento HTML sobre o mapa (OverlayView), e não um marcador do Google, porque o marcador só aceita imagem
 * de uma peça: sem anel que muda de cor, seta que gira nem texto. A animação mexe direto no DOM, sem passar pelo
 * Vue, então várias viaturas se movendo não re-renderizam telas inteiras.
 */
export default {
  name: 'FleetVehicleMarker',

  inject: { $mapPromise: { default: null } },

  props: {
    // Posição vinda do Frotas ({ lat, lng, identifier_code, stale, team, vehicle_type }).
    position: { type: Object, required: true },
    title: { type: String, default: '' },
    zIndex: { type: Number, default: 10 },
    highlighted: { type: Boolean, default: false },
    dimmed: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true },
    // Trajeto já conhecido ([{ lat, lng }]): dá a direção inicial, antes de haver movimento para medir.
    trail: { type: Array, default: () => [] },
  },

  computed: {
    target() {
      return { lat: Number(this.position.lat), lng: Number(this.position.lng) }
    },
    labelText() {
      const { team, identifier_code: code } = this.position

      return team && team.name ? team.name : code || ''
    },
    imageUrl() {
      const type = this.position.vehicle_type

      return type && type.image_url ? type.image_url : ''
    },
    // Tudo que muda o desenho: um só observador refaz o elemento.
    look() {
      return {
        color: fleetStateColor(this.position),
        label: this.showLabel ? this.labelText : '',
        image: this.imageUrl,
        highlighted: this.highlighted,
        dimmed: this.dimmed,
        title: this.title,
        zIndex: this.zIndex,
      }
    },
  },

  watch: {
    look: 'paint',
    // lat/lng em vez do objeto: o aviso troca o objeto a cada leva, mesmo sem a viatura ter andado.
    'position.lat': 'moveToTarget',
    'position.lng': 'moveToTarget',
    trail() {
      if (!this.glideFrame && !this.hasHeading) this.headingFromTrail()
    },
  },

  created() {
    // Fora do estado reativo de propósito: muda a cada quadro da animação.
    this.shown = this.target
    this.heading = 0
    this.hasHeading = false
    this.glideFrame = null
    this.overlay = null
    this.el = this.buildElement()
    this.headingFromTrail()
  },

  async mounted() {
    this.paint()

    if (!this.$mapPromise) return

    const map = await this.$mapPromise
    if (this._isDestroyed) return

    const Overlay = overlayClass()
    this.overlay = new Overlay(this.el, this.shown)
    this.overlay.setMap(map)
  },

  beforeDestroy() {
    this.stopGlide()
    if (this.overlay) this.overlay.setMap(null)
    this.overlay = null
  },

  methods: {
    buildElement() {
      const el = document.createElement('div')
      el.className = 'fvm'
      el.innerHTML = '<div class="fvm__dir"><i class="fvm__arrow"></i></div>'
        + `<div class="fvm__badge"><img class="fvm__img" alt="">${GLYPH}</div>`
        + '<div class="fvm__label"></div>'

      this.parts = {
        dir: el.querySelector('.fvm__dir'),
        img: el.querySelector('.fvm__img'),
        label: el.querySelector('.fvm__label'),
      }

      this.parts.img.addEventListener('error', () => el.classList.add('fvm--noimg'))
      el.addEventListener('click', event => {
        event.stopPropagation()
        this.$emit('click')
      })

      // Arrastar o mapa por cima da viatura não pode ser engolido por ela.
      const { google } = window
      if (google && google.maps && google.maps.OverlayView && google.maps.OverlayView.preventMapHitsAndGesturesFrom) {
        google.maps.OverlayView.preventMapHitsAndGesturesFrom(el)
      }

      return el
    },

    paint() {
      const { el, parts } = this
      const look = this.look

      el.style.setProperty('--fvm-color', look.color)
      el.style.zIndex = String(look.zIndex)
      el.title = look.title
      el.classList.toggle('fvm--focus', look.highlighted)
      el.classList.toggle('fvm--dim', look.dimmed)

      parts.label.textContent = look.label
      parts.label.style.display = look.label ? '' : 'none'

      if (look.image) {
        if (parts.img.getAttribute('src') !== look.image) {
          el.classList.remove('fvm--noimg')
          parts.img.setAttribute('src', look.image)
        }
      } else {
        parts.img.removeAttribute('src')
        el.classList.add('fvm--noimg')
      }

      parts.dir.style.transform = `rotate(${this.heading}deg)`
    },

    stopGlide() {
      if (this.glideFrame) cancelAnimationFrame(this.glideFrame)
      this.glideFrame = null
    },

    setHeading(degrees) {
      this.heading = degrees
      this.hasHeading = true
      this.parts.dir.style.transform = `rotate(${degrees}deg)`
    },

    moveToTarget() {
      this.stopGlide()

      const from = this.shown
      const to = this.target

      if (straightLineKm(from, to) >= MIN_HEADING_KM) this.setHeading(bearing(from, to))

      const start = performance.now()
      const step = now => {
        const t = Math.min(1, (now - start) / GLIDE_MS)
        const e = t * (2 - t) // desacelera ao chegar

        this.shown = {
          lat: from.lat + (to.lat - from.lat) * e,
          lng: from.lng + (to.lng - from.lng) * e,
        }
        if (this.overlay) this.overlay.moveTo(this.shown)

        this.glideFrame = t < 1 ? requestAnimationFrame(step) : null
      }
      this.glideFrame = requestAnimationFrame(step)
    },

    headingFromTrail() {
      const n = this.trail.length
      if (n < 2) return

      const a = this.trail[n - 2]
      const b = this.trail[n - 1]
      if (straightLineKm(a, b) >= MIN_HEADING_KM) this.setHeading(bearing(a, b))
    },
  },

  render(h) {
    return h()
  },
}
</script>

<style>
/* Não escopado de propósito: o elemento é criado fora da árvore do Vue e entregue ao Google Maps. */
.fvm {
  --fvm-size: 48px; /* mesmo tamanho-padrão do pin de ocorrência */
  --fvm-color: #28c76f;
  position: absolute;
  width: var(--fvm-size);
  height: var(--fvm-size);
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.fvm--focus .fvm__badge { box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.35), 0 1px 5px rgba(0, 0, 0, 0.4); }

.fvm--dim { opacity: 0.45; }

/* Gira inteira: a seta fica na borda, apontando para onde a viatura anda. */
.fvm__dir {
  position: absolute;
  inset: -10px;
  pointer-events: none;
  transition: transform 0.3s ease-out;
}

.fvm__arrow {
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 11px solid var(--fvm-color);
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.5));
}

.fvm__badge {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid var(--fvm-color);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  color: var(--fvm-color);
}

.fvm__img {
  width: 100%;
  height: 100%;
  padding: 2px;
  object-fit: contain;
}

.fvm__glyph {
  display: none;
  width: 70%;
  height: 70%;
}

.fvm--noimg .fvm__img { display: none; }
.fvm--noimg .fvm__glyph { display: block; }

.fvm__label {
  position: absolute;
  top: 100%;
  left: 50%;
  margin-top: 6px;
  padding: 1px 7px;
  transform: translateX(-50%);
  border: 1.5px solid var(--fvm-color);
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  color: #1a3c73;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  pointer-events: none;
}
</style>
